import dayjs from "dayjs";
import { useState } from "react";
import styled from "styled-components";

import { GoalDetailsBreakdownQuery, LinearChartSelection } from "@ctypes";
import { Translations, i18n } from "@i18n";
import { Card } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";
import { buildLinearChart, convertCentsToDollar, formatCurrency } from "@utils";

const HIST_W = 620;
const HIST_H = 170;
const MIN_HISTORY_DAYS = 30;

const SECTOR_COLORS = ["#9DCBBB", "#8FBFB0", "#7FB3A4", "#C9B08A", "#E09B7C"];
const SECTOR_LIMIT = 5;

const RANGES: Array<{ key: LinearChartSelection; label: string }> = [
  { key: "oneDay", label: "1D" },
  { key: "oneWeek", label: "1W" },
  { key: "oneMonth", label: "1M" },
  { key: "sixMonth", label: "6M" },
  { key: "oneYear", label: "1Y" },
  { key: "fiveYear", label: "All" },
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Panel = styled(Card)`
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PanelLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${home.ink44};
`;

const StatsRow = styled.div`
  display: flex;
  align-items: stretch;

  .stat {
    flex-grow: 1;
    flex-basis: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .label {
    font-size: 12px;
    color: ${home.ink46};
  }

  .value {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.5px;
  }

  .divider {
    width: 1px;
    background: ${home.line10};
    margin: 0 22px;
  }
`;

const DateAxis = styled.div`
  display: flex;
  align-items: center;
  height: 34px;
  padding-top: 12px;
  font-size: 12px;

  .start {
    color: ${home.ink44};
  }

  .mid {
    color: ${home.ink34};
    margin: 0 auto;
  }

  .end {
    font-weight: 500;
    color: ${home.ink62};
  }
`;

const RangeBar = styled.div`
  display: flex;
  gap: 3px;
  padding: 3px;
  border-radius: 999px;
  background: ${home.fill06};
`;

const RangePill = styled.button<{ $active: boolean }>`
  flex-grow: 1;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  font-family: ${home.sans};
  font-size: 12px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? home.fill14 : "transparent")};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active }) => ($active ? home.ink : home.ink52)};
`;

const Ledger = styled.div`
  display: flex;
  flex-direction: column;

  .row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 13px 0;
    border-bottom: 1px solid ${home.line08};
  }

  .row:last-child {
    border-bottom: none;
  }

  .label {
    font-size: 14px;
    color: ${home.ink58};
  }

  .value {
    font-size: 15px;
    font-weight: 600;
    margin-left: auto;
  }
`;

const EmptyNote = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px dashed ${home.line18};
  background: ${home.fill04};
  margin-top: auto;

  .text {
    font-size: 13px;
    line-height: 1.5;
    color: ${home.ink52};
  }
`;

const SectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  .sector {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 13px;
  }

  .name {
    color: ${home.ink80};
  }

  .name.muted {
    color: ${home.ink52};
  }

  .pct {
    font-weight: 600;
    margin-left: auto;
  }

  .rail {
    height: 5px;
    border-radius: 999px;
    background: ${home.fill07};
  }

  .fill {
    height: 5px;
    border-radius: 999px;
  }
`;

export const PerformanceHoldings = ({
  goalDetails,
  startDate,
}: {
  goalDetails?: GoalDetailsBreakdownQuery;
  startDate?: string | null;
}) => {
  const [range, setRange] = useState<LinearChartSelection>("fiveYear");

  const breakdown = goalDetails?.portfolioBreakdown;

  const netDeposits = convertCentsToDollar(breakdown?.contributionsInCents);
  const returns = convertCentsToDollar(breakdown?.returnsInCents);
  const value = convertCentsToDollar(breakdown?.totalValueInCents);

  const historyDays = startDate ? dayjs().diff(dayjs(startDate), "day") : 0;

  const selected = goalDetails?.[range];
  const chart = buildLinearChart({
    data: selected?.nodes,
    difference: selected?.grossReturnAbsolute,
    percentage: selected?.grossReturnPercentage,
    label: "",
    width: HIST_W,
    height: 130,
  });

  // The answer to "no data" is to say so – never draw a line anyway.
  const hasHistory = historyDays >= MIN_HISTORY_DAYS && chart.valid;

  const firstNode = chart.nodes[0];
  const midNode = chart.nodes[Math.floor(chart.nodes.length / 2)];
  const lastNode = chart.nodes[chart.nodes.length - 1];

  const sectors = [...(breakdown?.sectorExposures ?? [])]
    .filter((sector): sector is NonNullable<typeof sector> => Boolean(sector))
    .sort((a, b) => (b.factorPercentage ?? 0) - (a.factorPercentage ?? 0));

  const top = sectors.slice(0, SECTOR_LIMIT);
  const tail = sectors.slice(SECTOR_LIMIT);
  const tailPct = tail.reduce((sum, sector) => sum + (sector.factorPercentage ?? 0), 0);

  return (
    <Grid>
      <Panel>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PanelLabel>{i18n.t(Translations.GoalBreakdownRoutePerformance)}</PanelLabel>
          {hasHistory && startDate && (
            <span style={{ fontSize: 12, color: home.ink38, marginLeft: "auto" }}>
              {i18n.t(Translations.GoalBreakdownRouteSinceFirstDeposit, {
                date: dayjs(startDate).format("MMM YYYY"),
              })}
            </span>
          )}
        </div>

        {hasHistory ? (
          <>
            <StatsRow>
              <div className="stat">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteNetDeposits)}</span>
                <span className="value">{formatCurrency(netDeposits, true)}</span>
              </div>
              <div className="divider" />
              <div className="stat">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteReturns)}</span>
                <span className="value">
                  {returns < 0 ? "−" : ""}
                  {formatCurrency(Math.abs(returns), true)}
                </span>
              </div>
              <div className="divider" />
              <div className="stat">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteValueToday)}</span>
                <span className="value">{formatCurrency(value, true)}</span>
              </div>
            </StatsRow>

            <div>
              <svg
                width="100%"
                height={HIST_H}
                viewBox={`0 0 ${HIST_W} ${HIST_H}`}
                preserveAspectRatio="none"
                style={{ display: "block" }}
              >
                <defs>
                  <linearGradient id="fmGoalHist" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F2EFE9" stopOpacity="0.16" />
                    <stop offset="100%" stopColor="#F2EFE9" stopOpacity="0.01" />
                  </linearGradient>
                </defs>
                {chart.path && (
                  <g transform="translate(0, 10)">
                    <path d={`${chart.path} L${HIST_W},150 L0,150 Z`} fill="url(#fmGoalHist)" />
                    <path
                      d={chart.path}
                      fill="none"
                      stroke="rgba(242,239,233,0.78)"
                      strokeWidth="2"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                )}
                <line
                  x1="0"
                  y1={HIST_H - 10}
                  x2={HIST_W}
                  y2={HIST_H - 10}
                  stroke="rgba(242,239,233,0.16)"
                  strokeWidth="1"
                />
              </svg>

              <DateAxis>
                <span className="start">
                  {firstNode ? dayjs(firstNode.timestamp).format("MMM D, YYYY") : ""}
                </span>
                <span className="mid">
                  {midNode ? dayjs(midNode.timestamp).format("MMM D") : ""}
                </span>
                <span className="end">
                  {lastNode ? dayjs(lastNode.timestamp).format("MMM D, YYYY") : ""}
                </span>
              </DateAxis>
            </div>

            <RangeBar>
              {RANGES.map((item) => (
                <RangePill
                  key={item.key}
                  type="button"
                  $active={range === item.key}
                  onClick={() => setRange(item.key)}
                >
                  {item.label}
                </RangePill>
              ))}
            </RangeBar>
          </>
        ) : (
          <>
            <Ledger>
              <div className="row">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteNetDeposits)}</span>
                <span className="value">{formatCurrency(netDeposits, true)}</span>
              </div>
              <div className="row">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteReturns)}</span>
                <span className="value" style={{ color: home.ink72 }}>
                  {returns < 0 ? "−" : ""}
                  {formatCurrency(Math.abs(returns), true)}
                </span>
              </div>
              <div className="row">
                <span className="label">{i18n.t(Translations.GoalBreakdownRouteValueToday)}</span>
                <span className="value">{formatCurrency(value, true)}</span>
              </div>
            </Ledger>

            <EmptyNote>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={home.ink44}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5" />
                <path d="M12 16.5v.01" />
              </svg>
              <span className="text">
                {i18n.t(Translations.GoalBreakdownRouteNoChartYet, {
                  days: historyDays,
                  threshold: MIN_HISTORY_DAYS,
                })}
              </span>
            </EmptyNote>
          </>
        )}
      </Panel>

      <Panel>
        <PanelLabel>{i18n.t(Translations.GoalBreakdownRouteInvestedIn)}</PanelLabel>

        {sectors.length === 0 ? (
          <EmptyNote>
            <span className="text">{i18n.t(Translations.GoalBreakdownRouteNoHoldingsYet)}</span>
          </EmptyNote>
        ) : (
          <SectorList>
            {top.map((sector, index) => (
              <div key={sector.name ?? index} className="sector">
                <div className="head">
                  <span className="name">{sector.name}</span>
                  <span className="pct">{Math.round(sector.factorPercentage ?? 0)}%</span>
                </div>
                <div className="rail">
                  <div
                    className="fill"
                    style={{
                      width: `${Math.min(sector.factorPercentage ?? 0, 100)}%`,
                      background: SECTOR_COLORS[index % SECTOR_COLORS.length],
                    }}
                  />
                </div>
              </div>
            ))}

            {tail.length > 0 && (
              <div className="sector">
                <div className="head">
                  <span className="name muted">
                    {i18n.t(Translations.GoalBreakdownRouteOtherSectors, {
                      count: tail.length,
                    })}
                  </span>
                  <span className="pct" style={{ color: home.ink62 }}>
                    {Math.round(tailPct)}%
                  </span>
                </div>
                <div className="rail">
                  <div
                    className="fill"
                    style={{
                      width: `${Math.min(tailPct, 100)}%`,
                      background: "rgba(242,239,233,0.26)",
                    }}
                  />
                </div>
              </div>
            )}
          </SectorList>
        )}
      </Panel>
    </Grid>
  );
};
